import { useState, useCallback, useMemo } from 'react';


export interface UseTableSelectionOptions<T> {
  data: T[];
  getRowId: (row: T) => string | number;
  selectable: boolean;
  onSelectionChange?: (selected: T[]) => void;
  isLoading?: boolean;
  isRowSelectable?: (row: T) => boolean;
}

export interface UseTableSelectionReturn<T> {
  selectedIds: Set<string | number>;
  allVisibleSelected: boolean;
  isIndeterminate: boolean;
  hasSelectableRows: boolean;
  toggleSelectAll: () => void;
  toggleRow: (row: T) => void;
}

/**
 * Hook to manage table row selection state and logic
 * Handles single row selection, select all, and selection callbacks.
 *
 * 3-state toggle behavior for the header checkbox:
 *   - no selection (empty) → click → all visible selected
 *   - partial selection (indeterminate) → click → all visible selected
 *   - all visible selected → click → none selected
 */
export function useTableSelection<T extends Record<string, any>>(
  options: UseTableSelectionOptions<T>,
): UseTableSelectionReturn<T> {
  const {
    data, getRowId, selectable, onSelectionChange, isLoading = false, isRowSelectable,
  } = options;

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  const selectableData = useMemo(
    () => (isRowSelectable ? data.filter(isRowSelectable) : data),
    [data, isRowSelectable],
  );

  const allVisibleSelected = useMemo(() => (
    selectable &&
      selectableData.length > 0 &&
      selectableData.every((row) => selectedIds.has(getRowId(row)))
  ), [selectable, selectableData, selectedIds, getRowId]);

  const isIndeterminate = useMemo(
    () => selectable && selectedIds.size > 0 && !allVisibleSelected,
    [selectable, selectedIds.size, allVisibleSelected],
  );

  const toggleSelectAll = useCallback(() => {
    if (!selectable || isLoading) return;

    setSelectedIds((previousSelectedIds) => {
      const isAllSelected = selectableData.every((row) => previousSelectedIds.has(getRowId(row)));

      const nextSelectedIds = new Set(previousSelectedIds);

      if (isAllSelected) {
        // all → none
        for (const row of selectableData) nextSelectedIds.delete(getRowId(row));
      } else {
        // none or partial → all selectable
        for (const row of selectableData) nextSelectedIds.add(getRowId(row));
      }

      onSelectionChange?.(data.filter((row) => nextSelectedIds.has(getRowId(row))));
      return nextSelectedIds;
    });
  }, [selectable, data, selectableData, getRowId, onSelectionChange, isLoading]);

  const toggleRow = useCallback(
    (row: T) => {
      if (!selectable || isLoading) return;

      const id = getRowId(row);
      setSelectedIds((previousSelectedIds) => {
        const nextSelectedIds = new Set(previousSelectedIds);
        if (nextSelectedIds.has(id)) {
          nextSelectedIds.delete(id);
        } else {
          nextSelectedIds.add(id);
        }
        onSelectionChange?.(data.filter((r) => nextSelectedIds.has(getRowId(r))));
        return nextSelectedIds;
      });
    },
    [selectable, getRowId, data, onSelectionChange, isLoading],
  );

  return {
    selectedIds,
    allVisibleSelected,
    isIndeterminate,
    hasSelectableRows: selectableData.length > 0,
    toggleSelectAll,
    toggleRow,
  };
}
